import React from 'react';
import { Grid, IconButton, Tooltip, styled } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import AgentAvatar from '../AgentAvatar';
import { DashboardCard, CardTitle, StyledSkeleton, CenterGrid } from './CommonComponents';

const MODEL_LABELS = {
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o Mini',
  'gpt-4.1': 'GPT-4.1',
  'gpt-4.1-mini': 'GPT-4.1 Mini',
  'o3-mini': 'o3-mini',
  'claude-sonnet-4-20250514': 'Claude Sonnet 4',
  'claude-3-5-sonnet-20241022': 'Claude 3.5 Sonnet',
  'claude-3-7-sonnet-20250219': 'Claude 3.7 Sonnet',
  'gemini-2.0-flash': 'Gemini 2.0 Flash',
};

const AgentTile = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'background 120ms ease, border-color 120ms ease',
  '&:hover': {
    background: theme.palette.action.hover,
    borderColor: theme.palette.text.disabled,
  },
  '&:hover .agent-tile-actions': {
    opacity: 1,
  },
}));

const TileActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  opacity: 0,
  transition: 'opacity 120ms ease',
  flexShrink: 0,
});

const ViewAllLink = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  color: theme.palette.text.secondary,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 0',
  transition: 'color 120ms ease',
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));

const EmptyState = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: '32px 0',
  color: theme.palette.text.secondary,
  fontSize: 13,
}));

const CreateButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 14px',
  borderRadius: 6,
  border: `1px dashed ${theme.palette.divider}`,
  background: 'transparent',
  color: theme.palette.text.secondary,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 120ms ease',
  '&:hover': {
    borderColor: theme.palette.text.disabled,
    background: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
}));

export default function AgentCardsSection({ agents = [], loading = false }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <DashboardCard container rowGap="8px">
        <Grid item xs={12}>
          <CardTitle>Your Agents</CardTitle>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            {[0, 1, 2].map(i => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <StyledSkeleton variant="rectangular" height="44px" />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </DashboardCard>
    );
  }

  if (agents.length === 0) {
    return (
      <DashboardCard container>
        <Grid item xs={12}>
          <CardTitle>Your Agents</CardTitle>
        </Grid>
        <Grid item xs={12}>
          <EmptyState>
            <span>No agents yet</span>
            <CreateButton onClick={() => navigate('/agents')}>
              <AddIcon sx={{ fontSize: 15 }} />
              Create agent
            </CreateButton>
          </EmptyState>
        </Grid>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard container rowGap="8px">
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CardTitle>Your Agents</CardTitle>
        {agents.length > 6 && (
          <ViewAllLink onClick={() => navigate('/agents')}>
            View all <ArrowForwardIcon sx={{ fontSize: 14 }} />
          </ViewAllLink>
        )}
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          {agents.slice(0, 6).map(agent => {
            const modelLabel = MODEL_LABELS[agent.model] || agent.model || '';
            return (
              <Grid item xs={12} sm={6} md={4} key={agent.agentId}>
                <AgentTile onClick={() => navigate(`/channels?dmAgent=${agent.agentId}`)}>
                  <AgentAvatar name={agent.name} size={32} borderRadius="8px" systemAgent={agent.systemAgent} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 500,
                      fontSize: 13,
                      lineHeight: '18px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {agent.name || 'Untitled Agent'}
                    </div>
                    {modelLabel && (
                      <div style={{
                        fontSize: 11,
                        lineHeight: '14px',
                        color: 'var(--color-text-secondary, #71717a)',
                      }}>
                        {modelLabel}
                      </div>
                    )}
                  </div>
                  <TileActions className="agent-tile-actions">
                    <Tooltip title="Chat" placement="top">
                      <IconButton
                        size="small"
                        onClick={e => { e.stopPropagation(); navigate(`/channels?dmAgent=${agent.agentId}`); }}
                        sx={{ p: '4px' }}
                      >
                        <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Configure" placement="top">
                      <IconButton
                        size="small"
                        onClick={e => { e.stopPropagation(); navigate(`/agents/${agent.agentId}`); }}
                        sx={{ p: '4px' }}
                      >
                        <SettingsOutlinedIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </TileActions>
                </AgentTile>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </DashboardCard>
  );
}
